import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { FaroHttpCaptureConfig, pushLogToFaro, redactToJson } from 'tofe-core';

import { TOFE_SESSION_CONFIG } from '../tofe-session.module';

export const FARO_HTTP_CAPTURE_CONFIG = new InjectionToken<FaroHttpCaptureConfig | undefined>(
  'FARO_HTTP_CAPTURE_CONFIG',
);

/**
 * Forwards HTTP request/response payloads to Faro as structured log entries when
 * the URL matches the configured `includeUrls` regexes. Bodies are redacted via
 * the `redact` utility before serialization.
 *
 * Opt-in: no logs are pushed unless the capture config is enabled AND a URL
 * actually matches the include list.
 */
@Injectable()
export class FaroHttpLoggerInterceptor implements HttpInterceptor {
  private readonly sessionConfig = inject(TOFE_SESSION_CONFIG);
  private readonly capture = inject(FARO_HTTP_CAPTURE_CONFIG, { optional: true });

  private readonly includes: RegExp[];
  private readonly excludes: RegExp[];

  constructor() {
    this.includes = compilePatterns(this.capture?.includeUrls);
    this.excludes = compilePatterns(this.capture?.excludeUrls);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.shouldLog(request.url)) {
      return next.handle(request);
    }

    const cid = generateCorrelationId();
    const start = performance.now();
    const sanitizedUrl = sanitizeUrl(request.url);
    const maxBodyChars = this.capture?.maxBodyChars ?? 2048;
    const skipOverKb = this.capture?.skipBodyOverKb ?? 16;
    const redactKeys = this.capture?.redactKeys;

    if (this.capture?.captureRequestBody) {
      pushLogToFaro('http.request', 'info', {
        cid,
        method: request.method,
        url: sanitizedUrl,
        body: serializeBody(request.body, maxBodyChars, skipOverKb, redactKeys),
        ...(this.capture.captureRequestHeaders
          ? { headers: serializeHeaders(request.headers, maxBodyChars, redactKeys) }
          : {}),
      });
    }

    return next.handle(request).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse && this.capture?.captureResponseBody) {
            pushLogToFaro('http.response', 'info', {
              cid,
              status: String(event.status),
              durationMs: (performance.now() - start).toFixed(0),
              url: sanitizedUrl,
              body: serializeBody(event.body, maxBodyChars, skipOverKb, redactKeys),
            });
          }
        },
        error: (err: unknown) => {
          const status = err instanceof HttpErrorResponse ? err.status : 0;
          const body = err instanceof HttpErrorResponse ? err.error : err;
          pushLogToFaro('http.error', 'warn', {
            cid,
            status: String(status),
            durationMs: (performance.now() - start).toFixed(0),
            url: sanitizedUrl,
            body: serializeBody(body, maxBodyChars, skipOverKb, redactKeys),
          });
        },
      }),
    );
  }

  private shouldLog(url: string): boolean {
    if (!this.capture?.enabled) return false;
    if (!url.includes(this.sessionConfig.apiBaseUrl)) return false;
    if (this.excludes.some((re) => re.test(url))) return false;
    if (this.includes.length === 0) return false;
    return this.includes.some((re) => re.test(url));
  }
}

function compilePatterns(sources: string[] | undefined): RegExp[] {
  if (!sources?.length) return [];
  const compiled: RegExp[] = [];
  for (const source of sources) {
    try {
      compiled.push(new RegExp(source));
    } catch {
      // ignore malformed regex — defensive against config typos
    }
  }
  return compiled;
}

function sanitizeUrl(url: string): string {
  return url.split('?')[0];
}

function serializeBody(body: unknown, maxChars: number, skipOverKb: number, redactKeys: string[] | undefined): string {
  if (body === null || body === undefined) return '';
  if (typeof FormData !== 'undefined' && body instanceof FormData) return '[FormData]';
  if (typeof Blob !== 'undefined' && body instanceof Blob) return `[Blob ${body.size}b]`;
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return `[ArrayBuffer ${body.byteLength}b]`;
  }

  try {
    const rawSize = new Blob([typeof body === 'string' ? body : (JSON.stringify(body) ?? '')]).size / 1024;
    if (rawSize > skipOverKb) return `[skipped ${rawSize.toFixed(0)}KB]`;
  } catch {
    // size check failed, proceed and let redactToJson handle errors
  }

  return redactToJson(body, maxChars, redactKeys ? { redactKeys } : undefined);
}

function serializeHeaders(headers: HttpHeaders, maxChars: number, redactKeys: string[] | undefined): string {
  const obj: Record<string, string> = {};
  for (const name of headers.keys()) {
    obj[name] = headers.get(name) ?? '';
  }
  return redactToJson(obj, maxChars, redactKeys ? { redactKeys } : undefined);
}

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID — non-cryptographic correlation id.
  // eslint-disable-next-line sonarjs/pseudo-random
  return `cid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const FaroHttpLoggerInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: FaroHttpLoggerInterceptor,
  multi: true,
};
